import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Building2, ChevronLeftIcon, EllipsisVerticalIcon, PlusIcon } from 'lucide-react'
import {
	Badge,
	cn,
	ConfirmDialog,
	DealStageElement,
	DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
	IconButton,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
	Spinner,
	Stages,
	TabActionButton,
	Tabs, TabsList, TabsTrigger,
} from '@rentflowdev/ui'
import {
	useDeleteDealMutation,
	useGetDealQuery,
	useGetPropertyQuery,
	useUpdateDealStageMutation,
	useUpdateDealStatusMutation,
	useCreateDocumentMutation,
	useGenerateDocumentUploadUrlMutation,
	useUpdateDocumentMutation,
} from '@/api'
import { selectDealStageOverrides, setDealStage } from '@/store/deal-stage-overrides.slice'
import { propertyAddressWithUnit } from '@/lib/property-address'
import { toWizardProperty } from '@/components/create-document-wizard/from-property'
import { setSidebarOverride } from '@/store/sidebar-override.slice'
import {
	ACTIVE_STAGES, DEAL_STAGE_KEYS,
	DEAL_STATUSES, DEAL_STATUS_KEYS, DEAL_STATUS_VARIANTS, dealStatusForSelect,
} from '@/types'
import type { DealStage, DealStatus } from '@/types'
import { EditDealDialog } from '@/components/edit-deal-dialog'
import { StartRentalDialog } from '@/components/start-rental-dialog'
import { ConversationChat } from '@/components/conversation-chat'
import { DealInfoSection } from '@/components/deal-info-section'
import { DealDocumentsSection } from '@/components/deal-documents-section'
import { DealMeetingSection } from '@/components/deal-meeting-section'
import { DealNotesSection } from '@/components/deal-notes-section'
import { DealTasksSection } from '@/components/deal-tasks-section'
import { DealHistorySection } from '@/components/deal-history-section'

export const Route = createFileRoute('/_app/deals/$dealId')({
	component: DealDetailPage,
	// The tab lives in the URL so that returning from a document lands on
	// the tab the document was opened from, not on the default one.
	validateSearch: (search: Record<string, unknown>): { tab?: string } => ({
		tab: typeof search.tab === 'string' ? search.tab : undefined,
	}),
})

const DEAL_TABS = [
	{ value: 'info',      labelKey: 'deals.detail.tabs.info' },
	{ value: 'documents', labelKey: 'deals.detail.tabs.documents' },
	{ value: 'meetings',  labelKey: 'deals.detail.tabs.meetings' },
	{ value: 'tasks',     labelKey: 'deals.detail.tabs.tasks' },
	{ value: 'notes',     labelKey: 'deals.detail.tabs.notes' },
	{ value: 'history',   labelKey: 'deals.detail.tabs.history' },
] as const

const TABS_WITH_ADD: Record<string, string | undefined> = {
	documents: 'deals.documents.create',
	tasks:     'deals.tasks.add',
	notes:     'deals.notes.add',
	meetings:  'deals.meetings.add',
}

function DealDetailPage() {
	const { t } = useTranslation()
	const { dealId } = Route.useParams()
	const { data: apiDeal, isLoading } = useGetDealQuery(dealId)
	const { data: propertyData } = useGetPropertyQuery(apiDeal?.propertyId ?? '', { skip: !apiDeal?.propertyId })
	const navigate = useNavigate()
	const [updateStage] = useUpdateDealStageMutation()
	const [updateStatus] = useUpdateDealStatusMutation()
	const [deleteDeal] = useDeleteDealMutation()
	const dispatch = useDispatch()
	const overrides = useSelector(selectDealStageOverrides)
	const { tab: tabParam } = Route.useSearch()
	const tab = tabParam ?? 'info'
	const setTab = (next: string) => {
		void navigate({ to: '.', search: (prev: Record<string, unknown>) => ({ ...prev, tab: next }), replace: true })
	}
	const [closeDialogOpen, setCloseDialogOpen] = useState(false)
	const [chatCollapsed, setChatCollapsed] = useState(false)
	const [createDocOpen, setCreateDocOpen] = useState(false)
	const [addingNote,    setAddingNote]    = useState(false)
	const [addingTask,    setAddingTask]    = useState(false)
	const [addingMeeting, setAddingMeeting] = useState(false)
	const [editOpen, setEditOpen] = useState(false)
	const [deleteOpen, setDeleteOpen] = useState(false)
	const documentUploadRef = useRef<HTMLInputElement>(null)
	const [createDocument]   = useCreateDocumentMutation()
	const [generateUploadUrl] = useGenerateDocumentUploadUrlMutation()
	const [updateDocument]   = useUpdateDocumentMutation()

	// UI-augmented Deal — adds frontend-only display fields not yet on the backend.
	type EnrichedDeal = NonNullable<typeof apiDeal> & {
		propertyTitle?: string
		propertyAddress?: string
		clientName?: string
		clientCompany?: string
		messageCount?: number
	}
	const baseDeal = apiDeal as EnrichedDeal | null | undefined
	const deal: EnrichedDeal | undefined = baseDeal
		? overrides[dealId]
			? { ...baseDeal, stage: overrides[dealId] }
			: baseDeal
		: undefined

	useEffect(() => {
		if (!deal) return
		const childPath = deal.status === 'ACTIVE' ? '/deals/active' : '/deals/completed'
		dispatch(setSidebarOverride(childPath))
		return () => { dispatch(setSidebarOverride(null)) }
	}, [deal, dispatch])

	async function handleDealFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0]
		e.target.value = ''
		if (!file || !deal) return
		try {
			const { uploadUrl, publicUrl } = await generateUploadUrl({
				contentType: file.type,
				fileName:    file.name,
			}).unwrap()
			await new Promise<void>((resolve, reject) => {
				const xhr = new XMLHttpRequest()
				xhr.open('PUT', uploadUrl)
				xhr.setRequestHeader('Content-Type', file.type)
				xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`HTTP ${xhr.status}`)))
				xhr.onerror = () => reject(new Error('Network error'))
				xhr.send(file)
			})
			const doc = await createDocument({
				type:       'OTHER',
				title:      file.name,
				entityType: 'DEAL',
				entityId:   deal.id,
			}).unwrap()
			if (doc?.id) {
				await updateDocument({ id: doc.id, body: { fileUrl: publicUrl } }).unwrap()
			}
		} catch (err) {
			console.error('deal upload failed:', err)
		}
	}

	function handleStageClick(nextStage: DealStage) {
		if (!deal || nextStage === deal.stage) return
		dispatch(setDealStage({ id: dealId, stage: nextStage }))
		updateStage({ id: dealId, body: { stage: nextStage } })
	}

	if (isLoading && !deal) {
		return (
			<div className="flex h-full items-center justify-center">
				<Spinner />
			</div>
		)
	}

	if (!deal) {
		return (
			<div className="flex h-full items-center justify-center">
				<p className="text-sm text-(--color-muted-foreground)">{t('deals.notFound')}</p>
			</div>
		)
	}

	return (
		<div className="relative flex h-full flex-col overflow-hidden">
			<div className="flex flex-col gap-3 border-b border-(--color-border) p-4">
				<div className="flex min-w-0 items-start gap-3">
					<div className="flex-1 min-w-0 flex flex-col gap-1">
						<div className="flex items-center gap-3">
							<h1 className="text-2xl leading-8 tracking-[-0.6px] font-[500] text-(--color-foreground)">
								{t('deals.detail.titleWithClient', { client: deal.clientCompany ?? deal.clientName ?? deal.id })}
							</h1>
							<Badge variant={DEAL_STATUS_VARIANTS[deal.status]} size="sm">
								{t(DEAL_STATUS_KEYS[deal.status])}
							</Badge>
						</div>
						{deal.propertyTitle && (
							<button
								type="button"
								className="flex items-center gap-1.5 text-base leading-6 text-(--color-muted-foreground) hover:text-(--color-foreground) transition-colors"
								onClick={() => deal.propertyId && void navigate({ to: '/properties/$propertyId', params: { propertyId: deal.propertyId! } })}
							>
								<Building2 className="size-4 shrink-0" />
								{deal.propertyTitle}
							</button>
						)}
					</div>
					<Select
						value={dealStatusForSelect(deal.status)}
						onValueChange={(v) => {
							// Completing a deal means a rental starts. The kanban already
							// routes its "close" column through StartRentalDialog, whose
							// /rent/start orchestrator closes the deal server-side from
							// dealId — so don't set the status here too, or the deal would
							// be closed without ever creating the rental.
							if (v === 'COMPLETED') {
								setCloseDialogOpen(true)
								return
							}
							void updateStatus({ id: deal.id, body: { status: v as DealStatus } })
						}}
					>
						<SelectTrigger className="w-36 h-9">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{DEAL_STATUSES.map((s) => (
								<SelectItem key={s} value={s}>{t(DEAL_STATUS_KEYS[s])}</SelectItem>
							))}
						</SelectContent>
					</Select>
					<DropdownMenu>
						<DropdownMenuTrigger render={<IconButton variant="ghost" size="sm" aria-label={t('common.actions')} />}>
							<EllipsisVerticalIcon className="size-4" />
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end" className="min-w-44">
							<DropdownMenuItem onClick={() => setEditOpen(true)}>
								{t('deals.actionsMenu.edit')}
							</DropdownMenuItem>
							<DropdownMenuItem onClick={() => setDeleteOpen(true)}>
								{t('deals.actionsMenu.delete')}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>

				<Stages layout="row">
					{ACTIVE_STAGES.map((stage, i) => {
						const currentIdx = ACTIVE_STAGES.indexOf(deal.stage)
						const reached = currentIdx >= 0 && i <= currentIdx
						return (
							<DealStageElement
								key={stage}
								className="w-[212px]"
								type={reached ? 'selected' : 'default'}
								label={t(DEAL_STAGE_KEYS[stage])}
								onClick={() => handleStageClick(stage)}
							/>
						)
					})}
				</Stages>
			</div>

			<div className="border-b border-(--color-border) px-4">
				<Tabs value={tab} onValueChange={setTab}>
					<TabsList variant="line">
						{DEAL_TABS.map(({ value, labelKey }) => {
							const addKey = TABS_WITH_ADD[value]
							return (
								<TabsTrigger key={value} variant="line" value={value} className="group/tab gap-1.5">
									{t(labelKey)}
									{addKey && value === 'documents' && (
										<DropdownMenu>
											<DropdownMenuTrigger
												render={
													<TabActionButton ariaLabel={t(addKey)} />
												}
											>
												<PlusIcon className="size-3.5" />
											</DropdownMenuTrigger>
											<DropdownMenuContent align="start" className="min-w-40">
												<DropdownMenuItem onClick={() => setCreateDocOpen(true)}>
													{t('deals.documents.createFromTemplate')}
												</DropdownMenuItem>
												<DropdownMenuItem onClick={() => documentUploadRef.current?.click()}>
													{t('deals.documents.uploadFromComputer')}
												</DropdownMenuItem>
											</DropdownMenuContent>
										</DropdownMenu>
									)}
									{addKey && value === 'notes' && (
										<TabActionButton ariaLabel={t(addKey)} onClick={() => setAddingNote(true)}>
											<PlusIcon className="size-3.5" />
										</TabActionButton>
									)}
									{addKey && value === 'tasks' && (
										<TabActionButton ariaLabel={t(addKey)} onClick={() => setAddingTask(true)}>
											<PlusIcon className="size-3.5" />
										</TabActionButton>
									)}
									{addKey && value === 'meetings' && (
										<TabActionButton ariaLabel={t(addKey)} onClick={() => setAddingMeeting(true)}>
											<PlusIcon className="size-3.5" />
										</TabActionButton>
									)}
								</TabsTrigger>
							)
						})}
					</TabsList>
				</Tabs>
			</div>

			<div className="flex flex-1 overflow-hidden">
				<div
					className={cn(
						'overflow-auto p-4',
						tab === 'info' ? 'w-[676px] shrink-0' : 'flex-1 min-w-0',
					)}
				>
					{tab === 'info' && <DealInfoSection deal={deal} onOpenMeetings={() => setTab('meetings')} />}
					{tab === 'documents' && (
						<DealDocumentsSection
							deal={deal}
							property={propertyData ? toWizardProperty(propertyData, t) : undefined}
							createOpen={createDocOpen}
							onCreateOpenChange={setCreateDocOpen}
						/>
					)}
					{tab === 'notes' && <DealNotesSection dealId={deal.id} adding={addingNote} onAddingChange={setAddingNote} />}
					{tab === 'tasks' && <DealTasksSection dealId={deal.id} adding={addingTask} onAddingChange={setAddingTask} onOpenMeetings={() => setTab('meetings')} />}
					{tab === 'meetings' && <DealMeetingSection dealId={deal.id} propertyAddress={propertyData ? propertyAddressWithUnit(propertyData, t) : undefined} adding={addingMeeting} onAddingChange={setAddingMeeting} />}
					{tab === 'history' && <DealHistorySection dealId={deal.id} />}
				</div>

				{!chatCollapsed ? (
					<div className="ml-auto w-[524px] shrink-0 overflow-hidden border-l border-(--color-border)">
						<ConversationChat dealId={deal.id} onCollapse={() => setChatCollapsed(true)} />
					</div>
				) : (
					<div className="ml-auto shrink-0 p-4">
						<button
							type="button"
							onClick={() => setChatCollapsed(false)}
							className="flex items-center gap-1 rounded-(--radius-md) bg-(--color-secondary) px-2 py-1.5 text-sm font-[500] text-(--color-primary) transition-colors hover:bg-(--color-secondary-hover)"
							aria-label={t('deals.detail.chat.expand')}
						>
							<ChevronLeftIcon className="size-4" />
							<span className="px-1">{t('deals.detail.chat.label')}</span>
							{deal.messageCount != null && deal.messageCount > 0 && (
								<span className="inline-flex min-w-5 items-center justify-center rounded-full bg-(--color-destructive) px-1.5 text-xs leading-4 font-[600] text-(--color-primary-foreground)">
									{deal.messageCount}
								</span>
							)}
						</button>
					</div>
				)}
			</div>

			<input
				ref={documentUploadRef}
				type="file"
				className="hidden"
				accept="application/pdf,.doc,.docx,image/jpeg,image/png,image/webp,image/heic,image/heif"
				onChange={handleDealFilePicked}
			/>
			<EditDealDialog deal={deal} open={editOpen} onOpenChange={setEditOpen} />

			<ConfirmDialog
				open={deleteOpen}
				onOpenChange={setDeleteOpen}
				title={t('deals.deleteDialog.title')}
				description={t('deals.deleteDialog.description')}
				cancelLabel={t('common.cancel')}
				confirmLabel={t('deals.actionsMenu.delete')}
				variant="destructive"
				onConfirm={async () => {
					try {
						await deleteDeal(deal.id).unwrap()
						void navigate({ to: '/deals/active' })
					} catch (err) {
						console.error('deleteDeal failed:', err)
					}
				}}
			/>

			{/* Same path as the kanban's close column: /rent/start closes the deal
			    server-side once the rental is created. Stays mounted after closing
			    so its post-success warnings can render. */}
			<StartRentalDialog
				open={closeDialogOpen}
				onOpenChange={setCloseDialogOpen}
				preset={{
					contactId:  deal.contactId,
					propertyId: deal.propertyId,
					dealId:     deal.id,
					rentAmount: deal.amount ?? undefined,
				}}
			/>
		</div>
	)
}
